import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { deleteDocument } from '../services/Kyc'
import {
  ALERT_MESSAGES,
  DOCUMENT_STATUS_FOR_MODAL_TO_OPEN,
  DOCUMENT_VERIFICATION_STATUS,
  FILE_STATUS,
  STATUS,
  STEP1_IDENTITY_UPLOAD_FILES,
  STEP1_OTHER_UPLOAD_FILES,
} from '../enums/common'
import useStepFormHook from './useStepFormHook'
import { IUploadWithRetryFormState } from '../interfaces/Upload'
import { IStepProps } from '../interfaces/Step'
import { IStore } from '../interfaces/Store'
import { useTriggerChangeHook } from './triggerChangeHook'
import { getDocumentStatusTitle } from '../services/steps'
import useDocumentsModal from './useDocumentsModal'
import { loadingActions } from '../store/slices/Loading'
import { getUploadCount, setUploadCount } from '../services/utils'

const useStep1FormHook = (props: IStepProps) => {
  const globalDispatch = useDispatch()
  const [state, setState] = useState<IUploadWithRetryFormState[]>([
    {
      file: [null, null],
      status: STATUS.idle,
      toggleValidateButton: false,
      fileAlerts: [],
      fileStatus: [FILE_STATUS.pending],
      docInfo: [],
      validationButtonError: null,
      docValue: '',
      uploadCount: [],
      statusVerification: [],
      canRetry: [],
      docUserDetails: {},
      documentsUid: [],
    },
  ])
  const {
    deleteDocumentsReducerState,
    dispatch,
    getDocumentPreviews,
  } = useDocumentsModal()

  const {
    refreshDocumentsState,
    documentsPreviewUrl,
    openDocumentModal,
    previewIsLoading,
  } = deleteDocumentsReducerState
  const { form, onSubmit, NavButtonsProps } = useStepFormHook(props)
  const { watch, handleSubmit } = form
  const watchIdentityDocumentType = watch('identity_document_type')
  const watchOtherDocumentType = watch('other_input_type')
  const identity_document_type = useTriggerChangeHook('identity_document_type')
  const [documentState] = state
  const folder = useSelector(
    (stateStore: IStore) => stateStore.folderReducer.folder
  )

  const getFilesFromStateByType = () => {
    if (!watchIdentityDocumentType) {
      return documentState.file
    }
    const numberOfFiles =
      watchIdentityDocumentType === '4'
        ? STEP1_OTHER_UPLOAD_FILES[watchOtherDocumentType].length
        : STEP1_IDENTITY_UPLOAD_FILES[watchIdentityDocumentType].length

    const files = [...documentState.file]
    files.length = numberOfFiles

    return files
  }

  const validateFiles = () => {
    return state.every((item) => item.status === STATUS.validated)
  }

  const validateFile = (index: number) => {
    if (!state[index]) {
      throw new Error('Index not found')
    }
    let valid = true
    // const files = state[index].file
    const files = getFilesFromStateByType()

    files.forEach((file, i) => {
      if (!file) {
        valid = false
        setState((prevState) => {
          const indexPrevState = prevState[index]
          const newState = [...prevState]
          const { fileAlerts } = indexPrevState
          fileAlerts[i] = ALERT_MESSAGES.mandatory
          newState[index] = {
            ...indexPrevState,
            fileAlerts,
            validationButtonError: null,
          }
          return newState
        })
      }
    })

    return valid
  }

  const shouldOpenTheModal =
    documentState.statusVerification.every(
      (status) => DOCUMENT_STATUS_FOR_MODAL_TO_OPEN.indexOf(status) !== -1
    ) && getUploadCount() < 3

  const handleModalReUpload = async () => {
    dispatch({ type: 'handleModalReUpload' })
    const [firstDocumentUid] = documentState.documentsUid

    return deleteDocument({ documentUid: firstDocumentUid })
  }

  const handleModalOpen = async () => {
    const { documentsUid, file } = documentState
    const documentUid = documentsUid[0]
    dispatch({ type: 'resetDocumentsPreviewUrl' })
    dispatch({ type: 'previewIsLoading' })
    await getDocumentPreviews(documentUid)
    setUploadCount(getUploadCount() + 1)

    if (file.filter((value) => value !== null).length > 1) {
      await getDocumentPreviews(documentUid, true)
    }
    dispatch({ type: 'previewIsLoaded' })
  }

  useEffect(() => {
    if (openDocumentModal) {
      handleModalOpen()
    }
  }, [openDocumentModal])

  const removeInvalidDocument = () => {
    if (
      documentState.documentsUid.length ||
      !documentState.statusVerification.some(
        (status) => status < DOCUMENT_VERIFICATION_STATUS.VERIFIED_ELIGIBLE
      ) ||
      documentState.documentsUid.every((value) => value === null)
    ) {
      return Promise.resolve({})
    }

    const [firstDocumentUid] = documentState.documentsUid

    return deleteDocument({ documentUid: firstDocumentUid })
  }

  useEffect(() => {
    if (
      validateFiles() &&
      shouldOpenTheModal &&
      documentState.status === STATUS.validated
    ) {
      dispatch({ type: 'handleNextStep' })
    }
  }, [documentState.status])

  const shouldDisplayValidateDocuments: boolean = useSelector(
    (storeState: IStore) =>
      storeState.displayValidateDocumentsReducer.shouldDisplayValidateDocuments
  )

  const handleNextStep = async (event: React.MouseEvent<HTMLButtonElement>) => {
    const watchexistingIdentity = watch('existing_identity')
    if (
      (validateFiles() && !shouldOpenTheModal) ||
      !shouldDisplayValidateDocuments ||
      watchexistingIdentity === '1'
    ) {
      return
    }

    event.preventDefault()
    state.forEach((item, index: number) => {
      if (validateFile(index)) {
        setState((prevState) => {
          const indexPrevState = prevState[index]
          const newState = [...prevState]
          newState[index] = {
            ...indexPrevState,
            validationButtonError: ALERT_MESSAGES.validationButton,
          }
          return newState
        })
      }
    })
    await form.triggerValidation()
  }

  const handleModalDismiss = async () => {
    globalDispatch(loadingActions.loading())
    dispatch({ type: 'closeDocumentsModal' })

    return handleSubmit(onSubmit)()
  }

  return {
    identity_document_type,
    state,
    setState,
    onSubmit,
    NavButtonsProps,
    form,
    watchIdentityDocumentType,
    watchOtherDocumentType,
    handleNextStep,
    validateFile,
    openDocumentModal,
    handleModalReUpload,
    handleModalDismiss,
    refreshDocumentsState,
    removeInvalidDocument,
    documentsPreviewUrl,
    documentStatusTitle: getDocumentStatusTitle(documentState, folder),
    totalDocuments: documentsPreviewUrl.length,
    previewIsLoading,
  }
}

export default useStep1FormHook
